import React, { useMemo } from 'react';
import { Card, Row, Col, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faTimes } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';

const FilterBar = ({
  meters,
  filters,
  onFiltersChange,
  onClearFilters,
  financialYears,
  sources,
  subLocations
}) => {
  const currentFY = financialYears.sort(fy => fy.value)?.[0]?.value;
  // Extract unique values from meters for filter options
  const filterOptions = useMemo(() => {
    const modules = new Set();
    const categories = new Set();
    const subCategories = new Set();
    const kpis = new Set();

    meters.forEach(meter => {
      const fykpi = (meter.financialYearKpis || []).find(fykpi => fykpi.financialYear === currentFY);
      if (fykpi?.kpi?.module) modules.add(fykpi?.kpi?.module);
      if (fykpi?.kpi?.category) categories.add(fykpi?.kpi?.category);
      if (fykpi?.kpi?.subCategory) subCategories.add(fykpi?.kpi?.subCategory);
      if (fykpi?.kpi?.kpi) kpis.add(fykpi?.kpi?.kpi);
    });

    return {
      modules: Array.from(modules).sort(),
      categories: Array.from(categories).sort(),
      subCategories: Array.from(subCategories).sort(),
      kpis: Array.from(kpis).sort()
    };
  }, [meters]);

  const meterIdOptions = useMemo(() => {
    return meters.map(m => ({
      value: m.id,
      label: `${m.meterName} (${m.meterId})`
    }));
  }, [meters]);

  const selectedMeterIds = useMemo(() => {
    if (!filters.meterIds || filters.meterIds.length === 0) return [];
    return filters.meterIds.map(id => ({
      value: id,
      label: id
    }));
  }, [filters.meterIds]);

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value === '' ? undefined : value
    });
  };

  const handleMeterIdChange = (selectedOptions) => {
    const selectedIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    handleFilterChange('meterIds', selectedIds);
  };

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#3f88a5' : '#dee2e6',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(63, 136, 165, 0.25)' : 'none',
      '&:hover': {
        borderColor: '#3f88a5'
      }
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#e0f2f7',
      borderRadius: '4px'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#3f88a5',
      fontWeight: 600
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#3f88a5',
      ':hover': {
        backgroundColor: '#3f88a5',
        color: 'white'
      }
    })
  };

  return (
    <Card className="filter-card mb-4">
      <Card.Body>
        <Row>
          <Col md={3} className="mb-3 d-flex">
            <Button 
              variant="outline-secondary" 
              className="flex-grow-1" 
              onClick={onClearFilters}
            >
              <FontAwesomeIcon icon={faTimes} className="me-1" />
              Reset Filters
            </Button>
          </Col>
        </Row>

        <Row>
          {/* Meter ID Search */}
          <Col md={3} className="mb-3">
            <Form.Group>
              <Form.Label>Meter ID</Form.Label>
              <Select
                isMulti
                options={meterIdOptions}
                value={selectedMeterIds}
                onChange={handleMeterIdChange}
                placeholder="Select Meter IDs..."
                styles={customSelectStyles}
                className="react-select-container"
                classNamePrefix="react-select"
                isClearable
                closeMenuOnSelect={false}
                menuPortalTarget={document.body}
                menuPosition="fixed"  
              />
            </Form.Group>
          </Col>

          {/* Module Filter */}
          <Col md={3} className="mb-3">
            <Form.Group>
              <Form.Label>Module</Form.Label>
              <Form.Select
                value={filters.module || ''}
                onChange={(e) => handleFilterChange('module', e.target.value)}
              >
                <option value="">All Modules</option>
                {filterOptions.modules.map(module => (
                  <option key={module} value={module}>{module}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          {/* Category Filter */}
          <Col md={3} className="mb-3">
            <Form.Group>
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={filters.category || ''}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">All Categories</option>
                {filterOptions.categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          {/* Sub Category Filter */}
          <Col md={3} className="mb-3">
            <Form.Group>
              <Form.Label>Sub Category</Form.Label>
              <Form.Select
                value={filters.subCategory || ''}
                onChange={(e) => handleFilterChange('subCategory', e.target.value)}
              >
                <option value="">All Sub Categories</option>
                {filterOptions.subCategories.map(subCategory => (
                  <option key={subCategory} value={subCategory}>{subCategory}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          {/* KPI Filter */}
          <Col md={3} className="mb-3">
            <Form.Group>
              <Form.Label>KPI</Form.Label>
              <Form.Select
                value={filters.kpi || ''}
                onChange={(e) => handleFilterChange('kpi', e.target.value)}
              >
                <option value="">All KPIs</option>
                {filterOptions.kpis.map(kpi => (
                  <option key={kpi} value={kpi}>{kpi}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          {/* Location Filter */}
          {sources && (
            <Col md={3} className="mb-3">
              <Form.Group>
                <Form.Label>Location</Form.Label>
                <Form.Select
                  value={filters.sourceId || ''}
                  onChange={(e) => handleFilterChange('sourceId', parseInt(e.target.value) || '')}
                >
                  <option value="">All Locations</option>
                  {sources.map(source => (
                    <option key={source.value} value={source.value}>{source.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          )}

          {/* Sub Location Filter */}
          {subLocations && (
            <Col md={3} className="mb-3">
              <Form.Group>
                <Form.Label>Sub Location</Form.Label>
                <Form.Select
                  value={filters.subLocationId || ''}
                  onChange={(e) => handleFilterChange('subLocationId', parseInt(e.target.value) || '')}
                >
                  <option value="">All Sub Locations</option>
                  {subLocations.map(subLocation => (
                    <option key={subLocation.value} value={subLocation.value}>{subLocation.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          )}
        </Row>
      </Card.Body>
    </Card>
  );
};

export default FilterBar;